<script setup lang="ts">
import { ref } from 'vue';
import { useDatapackStore } from '../stores/useDatapackStore';
import FindBiomeDropdown from './dropdown/FindBiomeDropdown.vue';
import OpenDropdown from './dropdown/OpenDropdown.vue';
import { vOnClickOutside }from '@vueuse/components';
import { useSearchStore } from '../stores/useBiomeSearchStore';
import StructureDropdown from './dropdown/StructureDropdown.vue';
import { useSettingsStore } from '../stores/useSettingsStore';
import { EventTracker } from '../util/EventTracker';
import { useI18n } from 'vue-i18n';

const i18n = useI18n()

const datapackStore = useDatapackStore()
const searchStore = useSearchStore()
const settingsStore = useSettingsStore()

defineProps({
    detached: Boolean
})

const openDropdownOpen = ref(false)
const searchBiomeDropdownOpen = ref(false)
const structureDropdownOpen = ref(false)

function reload(event: MouseEvent){
    EventTracker.track("reload")
    datapackStore.$patch({})
}

function toggleBiomeSearch() {
    searchStore.disabled = !searchStore.disabled
    searchStore.$patch({})
}

function clearBiomeSearch() {
    searchStore.biomes.clear()
    searchStore.$patch({})
    searchBiomeDropdownOpen.value = false
}

function clearStructureSearch() {
    searchStore.structures.clear()
    searchStore.$patch({})
    structureDropdownOpen.value = false
}


</script>

<template>
    <div class="menu-root">
        <div class="buttons">
            <font-awesome-icon
                icon="fa-plus"
                class="button"
                tabindex="0"
                :class="{open: openDropdownOpen }"
                :title="i18n.t('menu.add.title')"
                @click="openDropdownOpen = true"
                @keypress.enter="openDropdownOpen = true"
            />
            <span class="group-button" :class="{active: !searchStore.disabled}">
                <font-awesome-icon
                    icon="fa-magnifying-glass"
                    class="button"
                    tabindex="0"
                    :class="{
                        open: searchBiomeDropdownOpen,
                        active: searchStore.biomes.size > 0
                    }"
                    :title="i18n.t('menu.search_biome.title')"
                    @click="searchBiomeDropdownOpen = true"
                    @keypress.enter="searchBiomeDropdownOpen = true"
                    @contextmenu.prevent="clearBiomeSearch"    
                    @dblclick.prevent="clearBiomeSearch"
                />
                <font-awesome-icon
                    v-if="searchStore.biomes.size > 0"
                    :icon="!searchStore.disabled ? 'fa-toggle-on' : 'fa-toggle-off'"
                    class="button transparent"
                    tabindex="0"
                    :title="i18n.t('menu.search_biome.toggle')"
                    @click.prevent="toggleBiomeSearch"
                />
            </span>
            <font-awesome-icon
                icon="fa-location-dot"
                class="button"
                tabindex="0"
                :class="{
                    open: structureDropdownOpen,
                active: searchStore.structures.size > 0
                }"
                :title="i18n.t('menu.structure_positions.title')"
                @click="structureDropdownOpen = true"
                @keypress.enter="structureDropdownOpen = true"
                @contextmenu.prevent="clearStructureSearch"    
                @dblclick.prevent="clearStructureSearch"
            />

            <font-awesome-icon v-if="settingsStore.dev_mode" icon="fa-rotate-right" class="button" tabindex="0" :title="i18n.t('menu.reload_datapacks.title')" @click="reload" @keypress.enter="reload" />
        </div>
        <div class="dropdowns" :class="{ detached }">
            <Transition>
                <OpenDropdown v-if="openDropdownOpen" v-on-click-outside="() => {openDropdownOpen=false}" @close="openDropdownOpen=false" tabindex="-1"/>
            </Transition>
            <Transition>
                <Suspense>
                    <FindBiomeDropdown v-if="searchBiomeDropdownOpen" v-on-click-outside="() => {searchBiomeDropdownOpen=false}" tabindex="-1"/>
                </Suspense>
            </Transition>
            <Transition>
                <Suspense>
                    <StructureDropdown v-if="structureDropdownOpen" v-on-click-outside="() => {structureDropdownOpen=false}" tabindex="-1"/>
                </Suspense>
            </Transition>
        </div>
    </div>
</template>

<style scoped>
.buttons
{
    display: flex;
    flex-direction: row;
    gap: 0.5rem;
    width: 100%;
    justify-content: center;
    position: relative;
}

.dropdowns{
    height: 0;
    width: 100%;
    position: relative;
}

.button {
    background-color: #ffffff;
    color: #334155;
    width: 1.5rem;
    height: 1.5rem;
    border-radius: 6px;
    padding: 0.5rem;
    transition: 0.2s;
    cursor: pointer;
    border: 1px solid #cbd5e1;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.button:hover,
.button.open {
    background-color: #f1f5f9;
    border-color: #94a3b8;
}

.button.active {
    background-color: #dff0e4;
    color: #2f6b43;
    border-color: #8fc39e;
}

.button.active:hover {
    background-color: #cce8d4;
}

.button.transparent {
    background-color: transparent;
}

.button.transparent:hover {
    background-color: transparent;
}

.group-button {
    display: flex;
    flex-direction: row;
    border-radius: 6px;
    background-color: transparent;
}

.group-button.active {
    background-color: transparent;
}

.spacer {
    width: 2rem;
}

.v-enter-active,
.v-leave-active {
  transition: opacity 0.2s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}

.dropdowns.detached {
    position: fixed;
    inset: 0;
    width: auto;
    height: 0;
    z-index: 5000;
    pointer-events: none;
}

.dropdowns.detached :deep(.dropdown) {
    position: fixed;
    left: 50%;
    top: 8.8rem;
    transform: translateX(-50%);
    width: min(30rem, calc(100vw - 2rem));
    max-height: min(36rem, calc(100vh - 12rem));
    pointer-events: auto;
}

@media (max-width: 900px) {
    .dropdowns.detached :deep(.dropdown) {
        top: 15.2rem;
        max-height: calc(100vh - 16.5rem);
    }
}

@media (max-width: 700px) {
    .dropdowns.detached :deep(.dropdown) {
        width: calc(100vw - 1rem);
    }
}

</style>
